import { api } from "utils/api";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { ProjectReview, ProjectReviewFormData } from "../types";

const createProjectReview = async (
  projectId: string,
  { isAnonymous, ...formData }: ProjectReviewFormData
) =>
  api.post<ProjectReview>(`/projects/${projectId}/reviews`, {
    body: JSON.stringify({ ...formData, isAnonymous: isAnonymous === "true" }),
  });

const useCreateProjectReview = () =>
  useMutation(
    ({
      projectId,
      formData,
    }: {
      projectId: string;
      formData: ProjectReviewFormData;
    }) => createProjectReview(projectId, formData)
  );

const updateProjectReview = async (
  projectId: string,
  reviewId: string,
  { isAnonymous, ...formData }: ProjectReviewFormData
) =>
  api.put<ProjectReview>(`/projects/${projectId}/reviews/${reviewId}`, {
    body: JSON.stringify({ ...formData, isAnonymous: isAnonymous === "true" }),
  });

const useUpdateProjectReview = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      projectId,
      reviewId,
      formData,
    }: {
      projectId: string;
      reviewId: string;
      formData: ProjectReviewFormData;
    }) => updateProjectReview(projectId, reviewId, formData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([
          "reviews",
          "PROJECT",
          data.reviewableId,
        ]);
      },
    }
  );
};

type WatchedTagValues = string[][];

/**
 * Transform a into expected shape for form fields
 */
const transformProjectReviewToFields = ({
  isAnonymous,
  ...rest
}: ProjectReview): ProjectReviewFormData => ({
  ...rest,
  isAnonymous: isAnonymous ? "true" : "false",
});

const useProjectReviewForm = (projectId: string, review?: ProjectReview) => {
  const { handleSubmit, watch, ...rest } = useForm<ProjectReviewFormData>({
    defaultValues: review
      ? transformProjectReviewToFields(review)
      : {
          roadmapTags: [],
          artTags: [],
          communityTags: [],
          founderTeamTags: [],
          isAnonymous: "false",
        },
  });

  const { mutate: create, ...createMutation } = useCreateProjectReview();
  const { mutate: update, ...updateMutation } = useUpdateProjectReview();

  const isEdit = !!review;
  const mutation = isEdit ? updateMutation : createMutation;

  // Create form submit handler that can handle either update or create mutate calls
  const onSubmit = handleSubmit((formData) =>
    isEdit
      ? update({ projectId, reviewId: review.type, formData })
      : create({ projectId, formData })
  );

  // Watch review tag checkbox values to disable when limit is reached
  const [
    artTagValues,
    communityTagValues,
    founderTeamTagValues,
    roadmapTagValues,
  ] = watch([
    "artTags",
    "communityTags",
    "founderTeamTags",
    "roadmapTags",
  ]) as WatchedTagValues;

  return {
    onSubmit,
    watching: {
      artTagValues,
      communityTagValues,
      founderTeamTagValues,
      roadmapTagValues,
    },
    mutation,
    ...rest,
  };
};

export { useProjectReviewForm };
